import React, { ReactNode, forwardRef, useImperativeHandle } from "react";
import Grid from "@mui/material/Grid";
import { Formik, FormikProps, FormikErrors, getIn, setIn } from "formik";
import { Box, Button, Divider, Typography } from "@mui/material";
import { FormField } from "./FormField";
import FormErrorSummary from "./FormErrorSummary";
import {
  getFormValidationSchema,
  getFormValues,
  MtcFormField,
  useFormStyles,
} from "./MtcFormFields";
import { FormProps } from "./FormProps";
import * as Yup from "yup";
import { VisibilityCondition } from "./VisibilityCondition";
import { validate as validateGuid } from "uuid";
import { FieldTypes } from "./FieldTypes";

interface ValidationTest {
  name: string;
  message: string;
  associatedFields?: string[];
  testFunction: (parent: Record<string, unknown>) => boolean;
}

type FormValues = Record<string, unknown>;

const Form = forwardRef(
  (
    props: FormProps & {
      customUI?: ReactNode;
      showButtons?: boolean;
      yupTests?: ValidationTest[];
      centerActionButtons?: boolean;
    },
    ref,
  ) => {
    const {
      fieldVariant,
      fields,
      onCancel,
      onSubmit,
      cancelButtonText,
      submitButtonText,
      triggers,
      customUI,
      showButtons = true,
      yupTests,
      actions,
      centerActionButtons = false,
    } = props;
    const { classes } = useFormStyles();

    const variant = fieldVariant ?? "outlined";

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formikRef.current) {
          formikRef.current.handleSubmit();
        }
      },
      discardForm: () => {
        if (formikRef.current) {
          discardForm(formikRef.current);
        }
      },
    }));

    function discardForm(formik: FormikProps<FormValues>) {
      formik.resetForm();
      onCancel?.();
    }

    const validationSchema = React.useMemo(
      () => getFormValidationSchema(fields ?? [], yupTests),
      [fields, yupTests],
    );
    const formikRef = React.useRef<FormikProps<FormValues>>(null);

    const isFieldVisible = (field: FormField, values: FormValues) => {
      // Question visibility check
      if (field.visibilityFunction && !field.visibilityFunction(values)) {
        return false;
      }

      if (field.visibilityConditions) {
        return field.visibilityConditions.every(
          (condition: VisibilityCondition) =>
            values[condition.fieldId] === condition.fieldAnswerValueString,
        );
      }

      return true;
    };

    /**
     * Validates only the VISIBLE fields — a hidden field's `required` must not block submission.
     *
     * The error map is NESTED (built with Formik's `setIn` from each Yup path), matching the shape
     * Formik keeps values/touched in — so every renderer reads errors the same way it reads values:
     * `getIn(formik.errors, field.id)`. A field-array subfield's path `"licenses[0].state"` nests
     * under `errors.licenses[0].state`; `getIn` walks straight to it. (This replaced an earlier FLAT
     * map keyed by the literal path, whose only justification was a DOM-name scroll in handleFormSubmit
     * — now gone; FormErrorSummary flattens the nested map itself to list the problems.)
     */
    const validate = (values: FormValues) => {
      let errors: FormikErrors<FormValues> = {};
      const addError = (path: string, message: string) => {
        errors = setIn(errors, path, message);
      };

      // Iterate over fields and validate only visible ones
      fields?.forEach((field: FormField) => {
        if (isFieldVisible(field, values)) {
          // Check for field arrays and dynamically build paths for validation
          if (field.type === FieldTypes.FieldArray) {
            const fieldArray = getIn(values, field.id); // Get the array of field items

            // Validate the ARRAY ITSELF, not only the subfields inside it. Rules that live on the
            // array — `.min(1, "Add at least one…")`, `.required()` — produce an error whose path is
            // the array's own id, and nothing used to look for it: the per-subfield loop below only
            // ever asked about `licenses[0].state`-shaped paths, and the cross-field pass at the
            // bottom only maps named yupTests. So an empty array silently passed validation and the
            // form submitted with nothing in it. (An array-level error and per-item errors are
            // mutually exclusive in practice: `.min(1)` only fires when the array is empty, and there
            // are then no items to produce subfield errors.)
            try {
              validationSchema.validateSyncAt(field.id, values);
            } catch (err) {
              if (err instanceof Yup.ValidationError && err.path) {
                addError(err.path, err.message);
              }
            }

            fieldArray.forEach((_: unknown, index: number) => {
              // For each subfield within the array item
              field.subfields?.forEach((subfield: FormField) => {
                const dynamicFieldId = `${field.id}[${index}].${subfield.id}`; // Construct dynamic subfield path
                try {
                  validationSchema.validateSyncAt(dynamicFieldId, values); // Validate individual subfield
                } catch (err) {
                  if (err instanceof Yup.ValidationError && err.path) {
                    // Capture all errors for the subfields
                    addError(err.path, err.message);
                  }
                }
              });
            });
          } else {
            // Regular field validation
            try {
              validationSchema.validateSyncAt(field.id, values);
            } catch (err) {
              if (err instanceof Yup.ValidationError && err.path) {
                addError(err.path, err.message);
              }
            }
          }
        }
      });

      // Run the overall object validation if needed (for cross-field checks)
      try {
        validationSchema.validateSync(values, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            // This allows us to validate against additional Yup tests
            if (error.path && !isGuid(error.path)) {
              const test = yupTests?.find((test) => test.name === error.path);
              if (test && test.associatedFields) {
                test.associatedFields.forEach((fieldId: string) => {
                  addError(fieldId, test.message);
                });
              }
            }
          });
        }
      }

      return errors;
    };

    /**
     * A failed submit is reported by FormErrorSummary, which renders above the fields, takes focus,
     * and links to every problem.
     *
     * This used to try to scroll to the first bad field itself, and failed at it twice over:
     * `submitForm()` was not awaited, so `formik.errors` was read a render too early and was still
     * `{}` on the first submit; and it looked the field up with `getElementsByName`, while the
     * renderers set `id`, not `name`. So on a long form — the patient referral runs to twenty-odd
     * questions — pressing Submit at the bottom did visibly nothing while the errors sat off-screen
     * above. Scrolling to one field would also fight the summary for focus, and the summary is the
     * better place to land: it names all of them.
     */
    const handleFormSubmit = async () => {
      await formikRef.current?.submitForm();
    };

    function isGuid(value: string): boolean {
      return validateGuid(value);
    }

    return (
      <Formik
        innerRef={formikRef}
        initialValues={getFormValues({}, props.fields ?? [])}
        validate={validate}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleFormSubmit();
            }}
          >
            {/* What is wrong, ABOVE the fields it is about.
                The per-field errors alone are not enough on a long form: the referral questionnaire
                runs to twenty-odd questions, so a required field near the top is simply off-screen
                when you submit from the bottom, and pressing Submit reads as "nothing happened".
                It sat under the fields until 2026-09-21 — which put the explanation below the
                thing needing fixing, so a long form scrolled you away from both. Above the form it
                is the first thing after the heading, and the links jump to each field.
                Only appears after a failed submit, so no existing form changes until then. */}
            <FormErrorSummary
              fields={fields ?? []}
              errors={formik.errors}
              submitCount={formik.submitCount}
            />

            <Grid container spacing={3}>
              {fields?.map((field: FormField) => {
                return (
                  <React.Fragment key={field.id}>
                    {field.header && (
                      <Grid
                        size={{ xs: 12, sm: 12 }}
                        key={field.id + "-header"}
                      >
                        <Typography variant="h5">{field.header}</Typography>
                        <Divider />
                      </Grid>
                    )}
                    <MtcFormField
                      formik={formik}
                      field={field}
                      classes={classes}
                      variant={variant}
                      triggers={triggers}
                    />
                  </React.Fragment>
                );
              })}
            </Grid>
            {customUI}

            {showButtons && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: centerActionButtons
                    ? "center"
                    : "space-between",
                  alignItems: "center",
                  marginTop: "auto",
                }}
              >
                {!centerActionButtons && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {actions?.map((action) => (
                      <Button
                        sx={{ margin: 0 }}
                        key={action.label}
                        type="button"
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={action.onClick}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  {onCancel && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      className={classes.button}
                      onClick={() => {
                        discardForm(formik);
                      }}
                    >
                      {cancelButtonText ?? "Cancel"}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    disabled={formik.isSubmitting}
                  >
                    {submitButtonText ?? "Save Changes"}
                  </Button>
                </Box>
              </Box>
            )}
          </form>
        )}
      </Formik>
    );
  },
);

export default Form;
